import { useCallback } from "react";

import { Button, Classes } from "@blueprintjs/core";
import classNames from "classnames";

import { RubricDetailQuery, TargetType, useRubricDetailQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

import { PreviewCriterionItem } from "./PreviewCriterionItem";

type Rubric = NonNullable<RubricDetailQuery["rubric"]>;

interface PreviewCriteriaListProps {
  rubric: Rubric;
  selectedCriteriaIds: string[];
  onChangeSelectedCriteriaIds: (ids: string[]) => void;
  target: { type: TargetType; id: string };
  onAddToTarget: () => void;
}

export function PreviewCriteriaList({
  rubric,
  selectedCriteriaIds,
  onChangeSelectedCriteriaIds,
  onAddToTarget,
  target,
}: PreviewCriteriaListProps) {
  const { data, loading } = useRubricDetailQuery({
    variables: { uuid: rubric.uuid },
    onCompleted: ({ rubric }) => {
      // Auto-select only criteria with items when data loads
      if (rubric?.evaluationCriteria && selectedCriteriaIds.length === 0) {
        const criteriaWithItems = ensureNonNullArray(
          rubric.evaluationCriteria,
        ).filter((c) => c?.items && c.items.length > 0);
        const ids = criteriaWithItems.map((c) => c?.id);
        onChangeSelectedCriteriaIds(ids);
      }
    },
  });

  const handleToggleCriterion = useCallback(
    (id: string) => {
      onChangeSelectedCriteriaIds(
        selectedCriteriaIds.includes(id)
          ? selectedCriteriaIds.filter((critId) => critId !== id)
          : [...selectedCriteriaIds, id],
      );
    },
    [selectedCriteriaIds, onChangeSelectedCriteriaIds],
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-4 overflow-y-auto p-6">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={classNames(Classes.SKELETON, "round-corners h-[120px]")}
          />
        ))}
      </div>
    );
  }

  const detailedRubric = data?.rubric || rubric;

  if (
    !detailedRubric.evaluationCriteria ||
    detailedRubric.evaluationCriteria.length === 0
  ) {
    return (
      <div className="p-6">No evaluation criteria found in this rubric.</div>
    );
  }

  const allCriteria = ensureNonNullArray(detailedRubric.evaluationCriteria);
  const criteriaWithItems = allCriteria.filter(
    (c) => c?.items && c.items.length > 0,
  );
  const hiddenCriteriaCount = allCriteria.length - criteriaWithItems.length;

  if (criteriaWithItems.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600">
          All criteria in this rubric are not fully set up and cannot be
          imported.
          <div className="mt-2 text-sm">
            Please add items to the criteria in the rubric before importing.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col justify-between gap-4 p-6">
      <div className="flex flex-col gap-4 overflow-y-auto">
        {criteriaWithItems.map((criterion) => (
          <PreviewCriterionItem
            key={criterion.id}
            criterion={criterion}
            isSelected={selectedCriteriaIds.includes(criterion.id)}
            onToggleSelection={handleToggleCriterion}
          />
        ))}
        {hiddenCriteriaCount > 0 && (
          <div className="mt-2 rounded-md bg-gray-100 px-4 py-3 text-sm text-gray-600">
            {hiddenCriteriaCount}{" "}
            {hiddenCriteriaCount === 1 ? "criterion" : "criteria"} hidden
            because {hiddenCriteriaCount === 1 ? "it is" : "they are"} not fully
            set up
          </div>
        )}
      </div>

      <div className="flex justify-start">
        <Button
          large={true}
          intent="primary"
          text={
            target.type === TargetType.Scenario
              ? "Add to Scenario"
              : "Add to Rubric"
          }
          onClick={onAddToTarget}
          disabled={selectedCriteriaIds.length === 0}
          style={{ marginRight: 16, width: "300px" }}
        />
      </div>
    </div>
  );
}
